/**
 * Free accounts (2026-10-05).
 *
 * A free account is a signed-in user with no subscriptions row. They can
 * look around the portal and upload one bill. The upload is parked in
 * bill_drafts and nobody works on it until they pay. After payment the
 * draft goes through the normal add-bill submit and becomes a real case.
 */

import posthog from 'posthog-js';
import { supabase } from './supabase';
import { API_BASE } from './apiConfig';
import { rememberedReferral } from './referralShare';
import { getMetaTrackingContext } from './checkoutContext';

export type BillDraft = {
  user_id: string;
  file_path: string;
  file_name: string | null;
  eob_path: string | null;
  /** More pages of the same bill. */
  extra_files: { path: string; name: string }[];
  provider_name: string | null;
  comment: string | null;
  /** YYYY-MM-DD, when the hospital wants paying by. */
  due_date: string | null;
  created_at: string;
};

/** What the free first look hands back. Mirrors FreeLook in
 *  apps/server/src/routes/freeCheck.ts. */
export type FreeLook = {
  provider: string | null;
  amountDue: number | null;
  documents: number;
  lineItems: number;
  checks: number;
  itemized: boolean;
  findings: { title: string; detail: string; amount: number | null }[];
  moreFindings: number;
  amountInQuestion: number | null;
  /** Where a specialist would start. Missing on looks saved before this existed. */
  routes?: { title: string; why: string }[];
  /** What bills this size usually come down by. */
  estimate?: { low: number; high: number } | null;
};

export type FreeCheckResult =
  | { status: 'ok'; look: FreeLook }
  /** unreadable: the reader couldn't make out the file (blurry, odd format).
   *  not_a_bill: it read the file and it isn't a medical bill. eob_only: it's
   *  an insurance statement, not the bill. nothing_to_read: a bill with no
   *  charges or amounts on it. limit: this account has used its free reads.
   *  failed: anything else, including a timeout. */
  | { status: 'unreadable' | 'not_a_bill' | 'eob_only' | 'nothing_to_read' | 'limit' | 'failed' };

export type StartCasePlan = 'one_off' | 'annual';

/** The bill this account uploaded before paying, if there is one. Returns
 *  null on any error too (for example before migration 103 is applied), so
 *  callers can treat "no draft" and "can't tell" the same way. */
export async function loadBillDraft(userId: string): Promise<BillDraft | null> {
  try {
    const { data, error } = await supabase
      .from('bill_drafts')
      .select('user_id, file_path, file_name, eob_path, extra_files, provider_name, comment, due_date, created_at')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) return null;
    if (!data) return null;
    return { ...(data as BillDraft), extra_files: Array.isArray(data.extra_files) ? data.extra_files : [] };
  } catch {
    return null;
  }
}

export async function saveBillDraft(
  draft: Omit<BillDraft, 'created_at'>,
  email: string | null | undefined,
): Promise<string | null> {
  const now = new Date().toISOString();
  // created_at is reset too: a replaced file restarts the reminder clock.
  const { error } = await supabase.from('bill_drafts').upsert(
    {
      ...draft,
      email: email ? email.trim().toLowerCase() : null,
      created_at: now,
      updated_at: now,
      reminders_sent: 0,
      last_reminder_at: null,
    },
    { onConflict: 'user_id' },
  );
  return error ? error.message : null;
}

/**
 * Asks the server for the free first look at the parked bill. The first
 * call for an upload reads the documents, which can take a minute. After
 * that the answer is stored and comes straight back.
 */
export async function runFreeCheck(): Promise<FreeCheckResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 120000);
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return { status: 'failed' };

    const res = await fetch(`${API_BASE}/free-check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: '{}',
      signal: controller.signal,
    });
    if (res.ok) {
      const body = (await res.json()) as { look?: FreeLook };
      return body.look ? { status: 'ok', look: body.look } : { status: 'failed' };
    }
    if (res.status === 422) {
      const { error } = (await res.json().catch(() => ({}))) as { error?: string };
      if (error === 'not_a_bill' || error === 'eob_only' || error === 'nothing_to_read') return { status: error };
      return { status: 'unreadable' };
    }
    if (res.status === 429) return { status: 'limit' };
    return { status: 'failed' };
  } catch {
    return { status: 'failed' };
  } finally {
    clearTimeout(timer);
  }
}

/** Removes the draft row. With removeFiles the uploaded files go too, which
 *  is what "replace the file" wants. After payment the files stay, because
 *  the new bills row points at them. */
export async function deleteBillDraft(draft: BillDraft, removeFiles: boolean): Promise<void> {
  if (removeFiles) {
    const paths = [draft.file_path, draft.eob_path, ...draft.extra_files.map((f) => f.path)].filter(
      (p): p is string => Boolean(p),
    );
    if (paths.length > 0) {
      await supabase.storage.from('medical_bills').remove(paths).catch(() => undefined);
    }
  }
  await supabase.from('bill_drafts').delete().eq('user_id', draft.user_id);
}

/**
 * Claims a paid subscription row that isn't linked to this user yet.
 * First by the checkout_ref we stored before going to Stripe, then by
 * email (the row's customer_email matches the login email, RLS allows
 * exactly that update). Returns true if the user has a subscription after.
 */
export async function linkPaidSubscription(userId: string, accessToken: string | null): Promise<boolean> {
  let checkoutRef: string | null = null;
  try {
    checkoutRef = localStorage.getItem('checkout_ref');
  } catch {
    /* no localStorage, the email fallback below still works */
  }

  if (checkoutRef && accessToken) {
    try {
      await fetch(`${API_BASE}/stripe/link-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ checkout_ref: checkoutRef }),
      });
    } catch {
      /* falls through to the email match */
    }
  }

  try {
    await supabase.from('subscriptions').update({ user_id: userId }).is('user_id', null).select('id');
  } catch {
    /* nothing to claim */
  }

  const { data } = await supabase.from('subscriptions').select('id').eq('user_id', userId).limit(1);
  return Boolean(data && data.length > 0);
}

/**
 * Sends a signed-in free account to Stripe. Uses the same public
 * create-checkout endpoint as the marketing site, so the webhook, the
 * purchase tracking and the success page all behave as they always have.
 * The ref is stored first so the subscription can be linked on the way back.
 */
export async function startCaseCheckout(plan: StartCasePlan, email: string | null | undefined): Promise<void> {
  const checkoutRef = crypto.randomUUID();
  // Note that they opened the payment page (migration 112). If they leave
  // without paying, the hourly tick writes to them about an hour later.
  // Best effort, it never holds up the checkout.
  try {
    const { data } = await supabase.auth.getUser();
    if (data.user) {
      await supabase
        .from('bill_drafts')
        .update({ checkout_started_at: new Date().toISOString() })
        .eq('user_id', data.user.id);
    }
  } catch {
    /* fine */
  }
  try {
    localStorage.setItem('checkout_ref', checkoutRef);
    localStorage.setItem('checkout_plan', plan);
  } catch {
    /* the email match in linkPaidSubscription covers this */
  }

  let posthogDistinctId: string | null = null;
  try {
    posthogDistinctId = posthog.get_distinct_id?.() ?? null;
  } catch {
    posthogDistinctId = null;
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/stripe/create-checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        plan,
        checkoutRef,
        email: email ?? null,
        source: 'app_free_account',
        posthogDistinctId,
        cancelTo: 'app',
        // Came in on a friend's link: they get the $40 off and the friend gets credit.
        referralCode: rememberedReferral(),
        ...getMetaTrackingContext(),
      }),
    });
  } catch (err) {
    // No response at all: the API is down or unreachable from here.
    console.error('[start-case] could not reach the API at', API_BASE, err);
    throw new Error('network');
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    console.error('[start-case] create-checkout failed', res.status, body);
    throw new Error(body.error ? `${res.status}: ${body.error}` : `create-checkout ${res.status}`);
  }
  const { url } = (await res.json()) as { url?: string | null };
  if (!url) {
    console.error('[start-case] create-checkout returned no url');
    throw new Error('create-checkout returned no url');
  }
  window.location.href = url;
}
